import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  ArrowRight, 
  MessageCircle, 
  Mail, 
  Linkedin, 
  ChevronDown,
  TrendingUp,
  BarChart3,
  Lightbulb,
  Users,
  Rocket,
  Menu,
  X,
  Check,
  Clock,
  Target,
  Zap
} from "lucide-react";
import { translations } from "./data/translations";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Button } from "./components/ui/button";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT = {
  email: "carolina.gonzalezb@outlook.com",
  linkedin: "https://www.linkedin.com/in/carolinagonzalezbetancur",
  whatsapp: "573124753860"
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_growth-revenue-lab/artifacts/ppst2sda_Carolina%20Gonzalez%20consultoria%20logo.png";

// Header Component
const Header = ({ language, setLanguage, t }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "header-premium" : "bg-transparent"
      }`}
      data-testid="main-header"
    >
      <div className="flex items-stretch h-20 lg:h-24">
        {/* Logo */}
        <a href="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
          <img 
            src={LOGO_URL} 
            alt="Carolina González Consultoría" 
            className="h-16 md:h-20 lg:h-24 w-auto"
          />
        </a>
        
        {/* Gold bar extending to the right */}
        <div className="flex-grow bg-[#B8A068] flex items-center justify-end px-6 lg:px-12">
          <nav className="hidden lg:flex items-center gap-10 mr-8" data-testid="desktop-nav">
            {["services", "work-together", "experience", "contact"].map((section) => (
              <button 
                key={section}
                onClick={() => scrollToSection(section)}
                className="text-sm font-medium text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors tracking-wide"
              >
                {t.nav[section]}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <div className="lang-toggle-gold" data-testid="language-toggle">
              <button 
                onClick={() => setLanguage("es")}
                className={language === "es" ? "active" : ""}
                data-testid="lang-es-btn"
              >
                ES
              </button>
              <button 
                onClick={() => setLanguage("en")}
                className={language === "en" ? "active" : ""}
                data-testid="lang-en-btn"
              >
                EN
              </button>
            </div>
            <button 
              onClick={() => scrollToSection("work-together")}
              className="bg-[#0A0A0A] text-white text-sm px-6 py-3 font-semibold hover:bg-[#1a1a1a] transition-colors"
              data-testid="header-cta-btn"
            >
              {t.nav.cta}
            </button>
          </div>

          {/* Mobile controls */}
          <div className="flex lg:hidden items-center gap-4">
            <div className="lang-toggle-gold" data-testid="mobile-language-toggle">
              <button 
                onClick={() => setLanguage("es")}
                className={language === "es" ? "active" : ""}
              >
                ES
              </button>
              <button 
                onClick={() => setLanguage("en")}
                className={language === "en" ? "active" : ""}
              >
                EN
              </button>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 -mr-2 text-[#0A0A0A]"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-menu lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            {["services", "work-together", "experience", "contact"].map((section) => (
              <button 
                key={section}
                onClick={() => scrollToSection(section)}
                className="text-left text-base font-medium py-3 hover:text-[#B8A068] transition-colors"
              >
                {t.nav[section]}
              </button>
            ))}
            <button 
              onClick={() => scrollToSection("work-together")}
              className="btn-primary text-center mt-4"
            >
              {t.nav.cta}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section - BOLD & Conversion Focused
const HeroSection = ({ t }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-16 bg-[#0A0A0A] overflow-hidden" data-testid="hero-section">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
      
      <div className="container-main relative z-10">
        <div className="max-w-5xl">
          <p className="text-[#B8A068] text-sm md:text-base font-semibold tracking-widest uppercase mb-6 opacity-0 animate-fade-in-up">
            {t.hero.credibility}
          </p>
          
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[0.95] tracking-tight opacity-0 animate-fade-in-up animation-delay-100"
            data-testid="hero-headline"
          >
            {t.hero.headline}
          </h1>
          
          <p 
            className="mt-8 text-lg md:text-xl lg:text-2xl text-[#999999] max-w-3xl leading-relaxed opacity-0 animate-fade-in-up animation-delay-200"
            data-testid="hero-subheadline"
          >
            {t.hero.subheadline}
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up animation-delay-300">
            <button 
              onClick={() => scrollToSection("session")}
              className="btn-cta-hero inline-flex items-center justify-center gap-3"
              data-testid="hero-cta-btn"
            >
              {t.hero.cta}
              <ArrowRight size={20} />
            </button>
            <button 
              onClick={() => scrollToSection("methodology")}
              className="btn-secondary-dark inline-flex items-center justify-center gap-2"
              data-testid="hero-secondary-btn"
            >
              {t.hero.secondary}
            </button>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 opacity-0 animate-fade-in animation-delay-500">
          <span className="text-xs text-[#666] tracking-widest uppercase">Scroll</span>
          <ChevronDown size={20} className="text-[#B8A068] animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// Industries Section - NEW
const IndustriesSection = ({ t }) => {
  const icons = [
    <Users key="1" className="w-8 h-8" />,
    <Lightbulb key="2" className="w-8 h-8" />,
    <BarChart3 key="3" className="w-8 h-8" />,
    <Zap key="4" className="w-8 h-8" />
  ];

  return (
    <section className="py-20 md:py-28 bg-[#0A0A0A]" data-testid="industries-section">
      <div className="container-main">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-16" data-testid="industries-title">
          {t.industries.title}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.industries.items.map((item, index) => (
            <div 
              key={index}
              className="p-8 border border-[#333] hover:border-[#B8A068] transition-all group"
              data-testid={`industry-card-${index}`}
            >
              <div className="w-14 h-14 bg-[#B8A068]/10 flex items-center justify-center mb-6 text-[#B8A068] group-hover:bg-[#B8A068] group-hover:text-white transition-all">
                {icons[index]}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{item.name}</h3>
              <p className="text-[#888]">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Target Client Section - NEW
const TargetClientSection = ({ t }) => {
  return (
    <section className="py-20 md:py-28 bg-[#FAFAF8]" data-testid="target-client-section">
      <div className="container-main">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-12" data-testid="target-client-title">
            {t.targetClient.title}
          </h2>

          <div className="space-y-4">
            {t.targetClient.items.map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-5 bg-white border border-[#EBE8E0] hover:border-[#B8A068] transition-all"
                data-testid={`target-item-${index}`}
              >
                <div className="w-6 h-6 bg-[#B8A068] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} className="text-white" />
                </div>
                <p className="text-lg text-[#0A0A0A]">{item}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-xl md:text-2xl font-medium text-center text-[#B8A068]">
            {t.targetClient.closing}
          </p>
        </div>
      </div>
    </section>
  );
};

// Problem Section - More Aggressive
const ProblemSection = ({ t }) => {
  return (
    <section className="py-20 md:py-28 bg-[#F5F3EE]" data-testid="problem-section">
      <div className="container-main">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-testid="problem-title">
            {t.problem.title}
          </h2>
          <p className="text-2xl md:text-3xl text-[#B8A068] font-medium mb-8">
            {t.problem.subtitle}
          </p>
          <p className="text-lg md:text-xl text-[#525252] mb-12 max-w-3xl mx-auto">
            {t.problem.text}
          </p>
          
          <div className="inline-block p-8 bg-[#0A0A0A] text-white">
            <p className="text-xl md:text-2xl font-bold">
              "{t.problem.differentiator}"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section - Outcome Driven
const ServicesSection = ({ t }) => {
  const icons = [
    <TrendingUp key="1" className="w-7 h-7" />,
    <BarChart3 key="2" className="w-7 h-7" />,
    <Target key="3" className="w-7 h-7" />,
    <Zap key="4" className="w-7 h-7" />,
    <Rocket key="5" className="w-7 h-7" />
  ];

  return (
    <section id="services" className="py-20 md:py-28 bg-[#0A0A0A]" data-testid="services-section">
      <div className="container-main">
        <div className="mb-16">
          <span className="text-[#B8A068] text-sm font-semibold tracking-widest uppercase">{t.services.title}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4" data-testid="services-title">
            {t.services.subtitle}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.items.map((service, index) => (
            <div 
              key={index}
              className="group p-8 lg:p-10 bg-[#111111] border border-[#222] hover:border-[#B8A068] transition-all duration-300"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-[#B8A068]/10 flex items-center justify-center mb-8 text-[#B8A068] group-hover:bg-[#B8A068] group-hover:text-white transition-all">
                {icons[index]}
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
              <p className="text-[#888] leading-relaxed">{service.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Work Together Section - Premium Service Tiers
const WorkTogetherSection = ({ t }) => {
  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="work-together" className="py-24 md:py-32 bg-[#FAFAF8] relative" data-testid="work-together-section">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(#0A0A0A 1px, transparent 1px)`,
        backgroundSize: '24px 24px'
      }} />
      
      <div className="container-main relative z-10">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" data-testid="work-together-title">
            {t.workTogether.title}
          </h2>
        </div>
        
        {/* Scarcity message */}
        <div className="text-center mb-16">
          <p className="inline-block px-6 py-3 bg-[#0A0A0A] text-white text-sm font-medium">
            {t.workTogether.scarcity}
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {t.workTogether.tiers.map((tier, index) => (
            <div 
              key={index}
              className={`relative flex flex-col ${
                tier.highlighted 
                  ? 'bg-[#0A0A0A] text-white lg:-mt-4 lg:mb-4 lg:py-12' 
                  : 'bg-white border border-[#EBE8E0]'
              } p-8 lg:p-10 transition-all duration-300 hover:shadow-xl group`}
              data-testid={`tier-card-${index}`}
            >
              {/* Label badge */}
              <div className={`inline-block self-start px-3 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6 ${
                tier.highlighted 
                  ? 'bg-[#B8A068] text-white' 
                  : 'bg-[#F5F3EE] text-[#B8A068]'
              }`}>
                {tier.label}
              </div>

              {/* Title */}
              <h3 className={`text-2xl lg:text-3xl font-bold mb-4 ${
                tier.highlighted ? 'text-white' : 'text-[#0A0A0A]'
              }`}>
                {tier.title}
              </h3>

              {/* Description */}
              <p className={`text-base mb-8 leading-relaxed ${
                tier.highlighted ? 'text-[#ccc]' : 'text-[#525252]'
              }`}>
                {tier.description}
              </p>

              {/* Includes */}
              <div className="space-y-3 mb-8 flex-grow">
                {tier.includes.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      tier.highlighted ? 'bg-[#B8A068]/20' : 'bg-[#F5F3EE]'
                    }`}>
                      <Check size={12} className={tier.highlighted ? 'text-[#B8A068]' : 'text-[#0A0A0A]'} />
                    </div>
                    <p className={`text-sm ${tier.highlighted ? 'text-[#ccc]' : 'text-[#525252]'}`}>
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              {/* Duration */}
              <div className={`flex items-center gap-2 mb-4 ${
                tier.highlighted ? 'text-[#888]' : 'text-[#525252]'
              }`}>
                <Clock size={16} />
                <span className="text-sm">{tier.duration}</span>
              </div>

              {/* Price */}
              <div className={`text-2xl lg:text-3xl font-bold mb-8 ${
                tier.highlighted ? 'text-[#B8A068]' : 'text-[#0A0A0A]'
              }`}>
                {tier.price}
              </div>

              {/* CTA Button */}
              <button 
                onClick={scrollToContact}
                className={`w-full py-4 font-bold text-sm tracking-wide transition-all duration-300 flex items-center justify-center gap-2 ${
                  tier.highlighted 
                    ? 'bg-[#B8A068] text-[#0A0A0A] hover:bg-[#c9b07a]' 
                    : 'bg-[#0A0A0A] text-white hover:bg-[#B8A068] hover:text-[#0A0A0A]'
                }`}
                data-testid={`tier-cta-${index}`}
              >
                {tier.cta}
                <ArrowRight size={16} />
              </button>

              {/* Highlighted card decoration */}
              {tier.highlighted && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#B8A068]" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Authority Section - Stronger
const AuthoritySection = ({ t }) => {
  return (
    <section className="py-20 md:py-28 bg-[#F5F3EE]" data-testid="authority-section">
      <div className="container-main">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-testid="authority-title">
            {t.authority.title}
          </h2>
          <p className="text-lg text-[#525252] max-w-2xl mx-auto">
            {t.authority.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {t.authority.items.map((item, index) => (
            <div 
              key={index}
              className="bg-white p-8 text-center border border-[#EBE8E0] hover:border-[#B8A068] transition-all"
              data-testid={`authority-item-${index}`}
            >
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A0A0A] mb-2">
                {item.number}
              </div>
              <p className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1">
                {item.label}
              </p>
              <p className="text-sm text-[#525252]">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Experience Section
const ExperienceSection = ({ t }) => {
  return (
    <section id="experience" className="py-20 md:py-28 bg-[#FAFAF8]" data-testid="experience-section">
      <div className="container-main">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-testid="experience-title">
            {t.experience.title}
          </h2>
          <p className="text-lg text-[#525252]">{t.experience.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {t.experience.items.map((item, index) => (
            <div 
              key={index}
              className="bg-white p-8 lg:p-10 border-l-4 border-[#B8A068] shadow-sm hover:shadow-lg transition-all"
              data-testid={`experience-card-${index}`}
            >
              <span className="text-xs font-semibold text-[#B8A068] tracking-widest uppercase">
                {item.industry}
              </span>
              <h3 className="text-2xl font-bold mt-2 mb-4">
                {item.company}
              </h3>
              <p className="text-[#525252] mb-6">{item.impact}</p>
              <div className="pt-4 border-t border-[#EBE8E0]">
                <p className="text-sm font-medium text-[#0A0A0A]">
                  <span className="text-[#B8A068]">Resultado:</span> {item.result}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Methodology Section
const MethodologySection = ({ t }) => {
  return (
    <section id="methodology" className="py-20 md:py-28 bg-[#0A0A0A]" data-testid="methodology-section">
      <div className="container-main">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="methodology-title">
              {t.methodology.title}
            </h2>
            <p className="text-lg text-[#888]">{t.methodology.subtitle}</p>
          </div>

          <div className="space-y-0">
            {t.methodology.steps.map((step, index) => (
              <div 
                key={index}
                className="flex gap-6 md:gap-10 pb-10 relative group"
                data-testid={`methodology-step-${index}`}
              >
                {index < t.methodology.steps.length - 1 && (
                  <div className="absolute left-6 top-16 bottom-0 w-px bg-[#333]" />
                )}
                <div className="w-12 h-12 bg-[#B8A068] flex items-center justify-center flex-shrink-0 z-10">
                  <span className="text-sm font-bold text-white">{step.number}</span>
                </div>
                <div className="pt-2">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#B8A068] transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-[#888]">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section - STRONGER & URGENT
const CTASection = ({ t }) => {
  return (
    <section className="py-20 md:py-28 bg-[#B8A068]" data-testid="cta-section">
      <div className="container-main text-center">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A0A0A] mb-4" data-testid="cta-title">
          {t.cta.title}
        </h2>
        <p className="text-xl text-[#0A0A0A]/80 mb-4 max-w-2xl mx-auto">
          {t.cta.subtitle}
        </p>
        <p className="text-lg font-semibold text-[#0A0A0A] mb-10">
          {t.cta.text}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white px-10 py-5 font-bold hover:bg-[#1a1a1a] transition-colors"
            data-testid="cta-primary-btn"
          >
            {t.cta.primary}
            <ArrowRight size={18} />
          </a>
          <a 
            href={`https://wa.me/${CONTACT.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-10 py-5 font-bold hover:bg-[#1da851] transition-colors"
            data-testid="cta-whatsapp-btn"
          >
            <MessageCircle size={18} />
            {t.cta.whatsapp}
          </a>
        </div>

        <p className="mt-10 text-sm text-[#0A0A0A]/60 font-medium">
          {t.scarcity.text}
        </p>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ t }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/leads`, formData);
      toast.success(t.contact.form.success);
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#FAFAF8]" data-testid="contact-section">
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <span className="text-[#B8A068] text-sm font-semibold tracking-widest uppercase">{t.contact.title}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-8" data-testid="contact-title">
              {t.contact.subtitle}
            </h2>
            
            <div className="space-y-6 mt-12">
              <a 
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-4 text-lg group"
                data-testid="contact-email-link"
              >
                <div className="w-14 h-14 bg-[#0A0A0A] flex items-center justify-center text-white group-hover:bg-[#B8A068] transition-colors">
                  <Mail className="w-6 h-6" />
                </div>
                <span className="group-hover:text-[#B8A068] transition-colors font-medium">{CONTACT.email}</span>
              </a>
              <a 
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-lg group"
                data-testid="contact-whatsapp-link"
              >
                <div className="w-14 h-14 bg-[#25D366] flex items-center justify-center text-white group-hover:scale-105 transition-transform">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <span className="group-hover:text-[#B8A068] transition-colors font-medium">WhatsApp</span>
              </a>
              <a 
                href={CONTACT.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-lg group"
                data-testid="contact-linkedin-link"
              >
                <div className="w-14 h-14 bg-[#0077B5] flex items-center justify-center text-white group-hover:scale-105 transition-transform">
                  <Linkedin className="w-6 h-6" />
                </div>
                <span className="group-hover:text-[#B8A068] transition-colors font-medium">LinkedIn</span>
              </a>
            </div>
          </div>

          <div className="bg-[#0A0A0A] p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <Input
                type="text"
                placeholder={t.contact.form.name}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="h-14 bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#666] focus:border-[#B8A068]"
                data-testid="form-name-input"
              />
              <Input
                type="email"
                placeholder={t.contact.form.email}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-14 bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#666] focus:border-[#B8A068]"
                data-testid="form-email-input"
              />
              <Input
                type="text"
                placeholder={t.contact.form.company}
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="h-14 bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#666] focus:border-[#B8A068]"
                data-testid="form-company-input"
              />
              <Textarea
                placeholder={t.contact.form.message}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#666] focus:border-[#B8A068] resize-none"
                data-testid="form-message-input"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-14 bg-[#B8A068] hover:bg-[#a08a58] text-[#0A0A0A] font-bold text-base"
                data-testid="form-submit-btn"
              >
                {loading ? "..." : t.contact.form.submit}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ t }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-12 bg-[#0A0A0A] text-white" data-testid="footer">
      <div className="container-main">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <img 
            src={LOGO_URL} 
            alt="Carolina González Consultoría" 
            className="h-12 w-auto brightness-0 invert"
          />
          <p className="text-sm text-[#666]">{t.footer.tagline}</p>
          <div className="flex items-center gap-5">
            <a 
              href={CONTACT.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#666] hover:text-white transition-colors"
              data-testid="footer-linkedin-link"
            >
              <Linkedin size={18} />
            </a>
            <a 
              href={`mailto:${CONTACT.email}`}
              className="text-[#666] hover:text-white transition-colors"
              data-testid="footer-email-link"
            >
              <Mail size={18} />
            </a>
          </div>
        </div>
        <div className="mt-10 pt-8 border-t border-[#222] text-center">
          <p className="text-xs text-[#666]">
            © {currentYear} Carolina González Consultoría. {t.footer.rights}.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp
const WhatsAppFloat = () => (
  <a 
    href={`https://wa.me/${CONTACT.whatsapp}`}
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    aria-label="Contact on WhatsApp"
    data-testid="whatsapp-float-btn"
  >
    <MessageCircle size={26} />
  </a>
);

// Sticky CTA (Mobile)
const StickyCTA = ({ t, visible }) => {
  const scrollToWorkTogether = () => {
    document.getElementById("work-together")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className={`sticky-cta md:hidden ${visible ? "visible" : ""}`} data-testid="sticky-cta">
      <button 
        onClick={scrollToWorkTogether}
        className="w-full py-4 bg-[#B8A068] text-[#0A0A0A] font-bold flex items-center justify-center gap-2"
      >
        {t.nav.cta}
        <ArrowRight size={16} />
      </button>
    </div>
  );
};

// Main App
function App() {
  const [language, setLanguage] = useState("es");
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-center" richColors />
      <Header language={language} setLanguage={setLanguage} t={t} />
      <main>
        <HeroSection t={t} />
        <IndustriesSection t={t} />
        <TargetClientSection t={t} />
        <ProblemSection t={t} />
        <ServicesSection t={t} />
        <WorkTogetherSection t={t} />
        <AuthoritySection t={t} />
        <ExperienceSection t={t} />
        <MethodologySection t={t} />
        <CTASection t={t} />
        <ContactSection t={t} />
      </main>
      <Footer t={t} />
      <WhatsAppFloat />
      <StickyCTA t={t} visible={showStickyCTA} />
    </div>
  );
}

export default App;
